import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, LockClosedIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState<number | undefined>(undefined);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check if account is locked due to too many attempts
  React.useEffect(() => {
    if (lockoutTime && Date.now() < lockoutTime) {
      const remainingTime = Math.ceil((lockoutTime - Date.now()) / 1000);
      if (remainingTime > 0) {
        const timer = setTimeout(() => {
          if (Date.now() >= lockoutTime) {
            setLockoutTime(undefined);
            setLoginAttempts(0);
          }
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [lockoutTime]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const validateForm = (): boolean => {
    const email = formData.email.trim();
    const password = formData.password;

    if (!email) {
      toast.error(t('authValidation.emailRequired'));
      return false;
    }

    if (!password) {
      toast.error(t('authValidation.passwordRequired'));
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('authValidation.validEmailRequired'));
      return false;
    }

    // Password validation
    if (password.length < 6) {
      toast.error(t('authValidation.passwordMin6'));
      return false;
    }

    if (password.length > 128) {
      toast.error(t('authValidation.passwordTooLong'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if account is locked
    if (lockoutTime && Date.now() < lockoutTime) {
      const remainingTime = Math.ceil((lockoutTime - Date.now()) / 1000);
      toast.error(t('loginValidation.tooManyAttempts', { time: remainingTime }));
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await login(formData.email.trim(), formData.password);
      toast.success(t('auth.welcomeBack'));
      setLoginAttempts(0);
      setLockoutTime(undefined);
      navigate('/');
    } catch (error: any) {
      // Increment failed attempts
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      // Lock account after 5 failed attempts for 15 minutes
      if (newAttempts >= 5) {
        const fifteenMinutesFromNow = Date.now() + 15 * 60 * 1000;
        setLockoutTime(fifteenMinutesFromNow);
        toast.error(t('loginValidation.tooManyAttemptsFull'));
      } else {
        toast.error(error?.response?.data?.message || t('auth.invalidCredentials'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--base)' }}
    >
      {/* Ambient glow — blue subtle */}
      <div
        style={{
          position: 'fixed',
          top: '-10%', right: '-5%',
          width: '50%', height: '70%',
          background: 'rgba(59, 130, 246, 0.06)',
          pointerEvents: 'none',
        }}
      />
      {/* Ambient glow — blue subtle */}
      <div
        style={{
          position: 'fixed',
          bottom: '-10%', left: '-5%',
          width: '40%', height: '60%',
          background: 'rgba(59, 130, 246, 0.05)',
          pointerEvents: 'none',
        }}
      />

      <div className="w-full max-w-sm animate-fade-up" style={{ position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <img
              src="/logo_principal.png"
              alt="Axioma"
              style={{ height: 64 }}
            />
            <div>
              <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.025em', marginBottom: 4 }}>
                {t('auth.welcomeBack')}
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{t('auth.signInToAccount')}</p>
            </div>
          </Link>
        </div>

        {/* Form card */}
        <div className="card" style={{ padding: '28px 28px 24px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Email */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                {t('auth.email')}
              </label>
              <div style={{ position: 'relative' }}>
                <EnvelopeIcon
                  style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--text-tertiary)' }}
                />
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="input"
                  style={{ paddingLeft: 40 }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.03em', textTransform: 'uppercase' }}>{t('auth.password')}</label>
                <button type="button" style={{ fontSize: 11, color: 'var(--text-tertiary)', transition: 'color 150ms', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {t('auth.forgot')}
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <LockClosedIcon
                  style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--text-tertiary)' }}
                />
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="input"
                  style={{ paddingLeft: 36, paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', cursor: 'pointer', background: 'none', border: 'none', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon style={{ width: 16, height: 16 }} /> : <EyeIcon style={{ width: 16, height: 16 }} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || !!(lockoutTime && Date.now() < lockoutTime)}
              className="btn btn-signal"
              style={{ width: '100%', marginTop: 4, justifyContent: 'center', opacity: isLoading || (lockoutTime && Date.now() < lockoutTime) ? 0.5 : 1 }}
            >
              {lockoutTime && Date.now() < lockoutTime ? (
                <>
                  {t('loginValidation.locked', { time: Math.ceil((lockoutTime - Date.now()) / 1000) })}
                </>
              ) : isLoading ? (
                <>
                  <div className="spinner" style={{ width: 14, height: 14 }} />
                  {t('auth.signingIn')}
                </>
              ) : (
                t('auth.login')
              )}
            </button>
          </form>

          <div style={{ borderTop: '1px solid var(--border-default)', marginTop: 24, paddingTop: 20, textAlign: 'center' }}>
            <button
              disabled
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toast.error('Google login is currently disabled');
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid var(--border-default)',
                borderRadius: '8px',
                background: 'var(--surface-2)',
                color: 'var(--text-muted)',
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '16px',
                pointerEvents: 'none'
              }}
            >
              <span style={{ fontSize: '18px' }}>G</span>
              Continue with Google
            </button>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {t('auth.noAccount')}{' '}
              <Link to="/register" style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>
                {t('auth.createOne')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
